/**
 * Portfolio Configuration File
 *
 * This file contains all the data and configuration for the portfolio website and resume.
 * ALL configurable values are centralized here to avoid duplication across components.
 *
 * Configuration Sections:
 * 1. Site Configuration - Meta data, SEO, favicon, etc.
 * 2. Theme Settings - Colors, fonts, spacing, animations
 * 3. Personal Information - Contact details, social links
 * 4. Content Data - Projects, skills, experience
 * 5. UI Configuration - Component settings, layout options
 * 6. Resume Settings - PDF theme, export options
 *
 * To update your portfolio:
 * 1. Update site config in 'siteConfig' section
 * 2. Modify theme settings in 'theme' section
 * 3. Update personal info in the 'about' and 'resume.personalInfo' sections
 * 4. Add/remove projects in the 'projects' array
 * 5. Add/remove skills in the 'skills' array
 * 6. Update work experience in 'resume.experience'
 * 7. Add certifications in 'resume.certifications'
 * 8. Adjust UI settings in 'uiConfig' section
 *
 * The entire website and all components automatically use this centralized configuration.
 */

/**
 * Site Configuration
 * Global settings for SEO, meta tags, favicon, and site behavior
 */
const siteConfig = {
  // Basic site information
  siteName: 'Abhinav Chaturvedi - Portfolio',
  siteUrl: 'https://abhinavchat.github.io/abhinavchat-portfolio',
  siteDescription: 'Abhinav Chaturvedi - Solution Architect II with 12.5+ years of experience designing cutting-edge engineering solutions, full-stack development, and large-scale distributed infrastructure.',

  // SEO and meta tags
  meta: {
    keywords: 'Solution Architect, Full Stack Developer, Data Science, Machine Learning, Python, React, AWS, Software Engineering',
    author: 'Abhinav Chaturvedi',
    ogType: 'website',
    ogImage: '/og-image.jpg',
    twitterHandle: '@abhinavchat'
  },

  // Favicon configuration
  favicon: {
    ico: '/favicon.ico',
    png192: '/logo192.png',
    png512: '/logo512.png',
    appleTouchIcon: '/logo192.png'
  },

  // Google Analytics (optional)
  analytics: {
    enabled: false,
    gtag: '' // Add your Google Analytics ID here
  },

  // Repository information
  repository: {
    url: 'https://github.com/abhinavchat/abhinavchat-portfolio',
    branch: 'main'
  }
}

/**
 * Theme Configuration
 * Colors, fonts, spacing, and visual design settings
 */
const theme = {
  // Color palette
  colors: {
    light: {
      primary: '#3182ce',
      primaryHover: '#2c5aa0',
      accent: '#ed8936',
      success: '#38a169',
      background: '#fafafa',
      backgroundAlt: '#ffffff',
      backgroundCard: '#ffffff',
      foreground: '#2d3748',
      foregroundAlt: '#4a5568',
      foregroundMuted: '#718096',
      border: '#e2e8f0',
      shadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
      shadowLg: 'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      shadowXl: 'rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px'
    },
    dark: {
      primary: '#60a5fa',
      primaryHover: '#3b82f6',
      accent: '#f59e0b',
      success: '#10b981',
      background: '#0f172a',
      backgroundAlt: '#1e293b',
      backgroundCard: '#334155',
      foreground: '#f1f5f9',
      foregroundAlt: '#e2e8f0',
      foregroundMuted: '#94a3b8',
      border: '#475569',
      shadow: 'rgba(0, 0, 0, 0.25) 0px 4px 6px -1px, rgba(0, 0, 0, 0.1) 0px 2px 4px -1px',
      shadowLg: 'rgba(0, 0, 0, 0.25) 0px 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -2px',
      shadowXl: 'rgba(0, 0, 0, 0.25) 0px 20px 25px -5px, rgba(0, 0, 0, 0.1) 0px 10px 10px -5px'
    }
  },

  // Typography
  fonts: {
    primary: "'Inter', 'Poppins', sans-serif",
    secondary: "'Times New Roman', serif",
    mono: "'Fira Code', 'Monaco', monospace"
  },

  // Spacing and layout
  spacing: {
    borderRadius: '12px',
    borderRadiusLg: '16px',
    sectionSpacing: '6rem',
    cardSpacing: '2rem',
    maxWidth: '1200px'
  },

  // Animation settings
  animations: {
    duration: '0.3s',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    stagger: '0.1s'
  },

  // Breakpoints for responsive design
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px'
  }
}

/**
 * UI Configuration
 * Component settings, layout options, and interface behavior
 */
const uiConfig = {
  // Header settings
  header: {
    height: {
      desktop: '8em',
      tablet: '6em',
      mobile: '5em'
    },
    sticky: true,
    showOnScroll: true,
    blurEffect: true
  },

  // Navigation settings
  navigation: {
    showProjectsLink: true,
    showSkillsLink: true,
    showContactLink: true,
    showResumeLink: true,
    mobileMenuOverlay: true
  },

  // Footer settings
  footer: {
    showCopyright: true,
    showSourceLink: true,
    showBuiltWith: false
  },

  // Animation preferences
  animations: {
    enabled: true,
    reduceMotionQuery: true, // Respect prefers-reduced-motion
    pageLoadAnimation: true,
    sectionRevealAnimation: true,
    hoverEffects: true
  },

  // Scroll behavior
  scroll: {
    smoothScrolling: true,
    scrollToTopButton: true,
    scrollToTopThreshold: 500
  },

  // Contact form (if implemented)
  contact: {
    showForm: false, // Currently using email link
    emailService: 'emailjs', // For future implementation
    formValidation: true
  }
}

/**
 * Resume PDF Configuration
 * Settings for PDF generation and export
 */
const resumeConfig = {
  // Default theme for PDF generation
  defaultTheme: 'professional',

  // PDF generation settings
  pdf: {
    format: 'a4',
    orientation: 'portrait',
    margins: {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20
    },
    scale: 2, // For high resolution
    filename: 'Abhinav_Chaturvedi_Resume_Professional.pdf'
  },

  // Export options
  exports: {
    pdf: true,
    json: true,
    print: true,
    themes: false // Disable theme selector for now
  },

  // Section visibility in PDF
  sections: {
    personalInfo: true,
    summary: true,
    experience: true,
    education: true,
    skills: true,
    certifications: true,
    projects: false // Exclude from PDF to keep it concise
  }
}

const header = {
  // all the properties are optional - can be left empty or deleted
  homepage: process.env.PUBLIC_URL + '/',
  title: 'AC.',
}

const about = {
  // all the properties are optional - can be left empty or deleted
  name: 'Abhinav Chaturvedi',
  role: 'Solution Architect',
  description:
    'As a seasoned Full Stack Developer with over 12.5+ years of experience, I am passionate about designing cutting-edge engineering solutions that prioritize user experience while leveraging my expertise in full-stack development. Additionally, I have extensive experience architecting and designing large-scale, distributed, and scalable infrastructure for complex projects.',
  resume: process.env.PUBLIC_URL + '/resume',
  social: {
    linkedin: 'https://www.linkedin.com/in/abhinavchat/',
    github: 'https://github.com/abhinavchat',
  },
}

const projects = [
  // projects can be added an removed
  // if there are no projects, Projects section won't show up
  {
    name: 'Smart Health',
    description:
      'Connect insurance companies and healthcare providers to streamline patient data sharing with real-time updates and a single identifier.',
    stack: ['Python', 'FastAPI', 'React', 'PostgreSQL', 'AWS', 'Docker'],
    sourceCode: '',
    livePreview: 'https://smart.health/',
  },
  {
    name: 'Analytx',
    description:
      'A No-Code platform as a service (PaaS) facilitating business owners to leverage Machine Learning/AI to make business decisions.',
    stack: ['Python', 'Java', 'Spring Boot', 'AngularJS', 'Docker', 'AWS'],
    sourceCode: '',
    livePreview: 'http://54.88.44.99/analytx/login',
  },
  {
    name: 'Enrollment Prediction - Otsuka',
    description:
      'Forecasting the number of enrollments for a site in a clinical trial based on stochastic approaches using Poisson Process and pseudo-Monte Carlo simulations.',
    stack: ['Python', 'TensorFlow', 'Pandas', 'NumPy', 'Jupyter'],
    sourceCode: '',
    livePreview: '',
  },
  {
    name: 'KxDocs',
    description:
      'KXDocs is cloud-based knowledge management system which turns an organizations library of documents into more productive "knowledge capital"',
    stack: ['.NET', 'AngularJS', 'SQL Server', 'ElasticSearch'],
    sourceCode: '',
    livePreview: 'https://www.kxdocs.com',
  },
]

const youtube = [
  // content can be added an removed
  // if there are no videos, Content section won't show up
  {
    title: 'RabbitMQ 101 | Tutorial with Python',
    embed_url: "https://www.youtube.com/embed/1KYxYIZtpkE"
  }
];

const skills = [
  // API Development Skills
  {
    title: 'Python',
    competency: 5,
    category: ['Languages', 'API Development', 'Data Science'],
  },
  {
    title: 'FastAPI',
    competency: 5,
    category: ['API Development', 'Python', 'Web Development'],
  },
  {
    title: 'Flask',
    competency: 4,
    category: ['API Development', 'Python', 'Web Development'],
  },
  {
    title: 'Java',
    competency: 4,
    category: ['Languages', 'API Development'],
  },
  {
    title: 'AngularJS',
    competency: 3,
    category: ['Web Development', 'Javascript'],
  },
  {
    title: 'MySQL',
    competency: 4,
    category: ['Databases', 'API Development'],
  },
  {
    title: 'PostgreSQL',
    competency: 4,
    category: ['Databases', 'API Development'],
  },
  {
    title: 'MongoDB',
    competency: 4,
    category: ['Databases', 'API Development'],
  },
  {
    title: 'Swagger/OpenAPI',
    competency: 4,
    category: ['API Development', 'Documentation'],
  },
  {
    title: 'Rust',
    competency: 2,
    category: ['Languages', 'API Development'],
  },
  // Data Science Skills
  {
    title: 'TensorFlow 2.0',
    competency: 5,
    category: ['Data Science', 'Machine Learning'],
  },
  {
    title: 'Keras',
    competency: 4,
    category: ['Data Science', 'Machine Learning'],
  },
  {
    title: 'PySpark',
    competency: 4,
    category: ['Data Science', 'Big Data'],
  },
  {
    title: 'Pandas',
    competency: 5,
    category: ['Data Science', 'Python'],
  },
  {
    title: 'NumPy',
    competency: 4,
    category: ['Data Science', 'Python'],
  },
  {
    title: 'Scikit-Learn',
    competency: 4,
    category: ['Data Science', 'Machine Learning'],
  },
  {
    title: 'Dask',
    competency: 3,
    category: ['Data Science', 'Big Data'],
  },
  {
    title: 'Jupyter Notebooks',
    competency: 4,
    category: ['Data Science', 'Tools'],
  },
  {
    title: 'Hugging Face',
    competency: 3,
    category: ['Data Science', 'NLP', 'Machine Learning'],
  },
  {
    title: 'LangChain',
    competency: 3,
    category: ['Data Science', 'LLM', 'AI'],
  },
  {
    title: 'Vector Databases (FAISS, Qdrant)',
    competency: 3,
    category: ['Data Science', 'Databases', 'AI'],
  },
  // Microservices Skills
  {
    title: 'REST APIs',
    competency: 5,
    category: ['Microservices', 'API Development'],
  },
  {
    title: 'gRPC',
    competency: 3,
    category: ['Microservices', 'API Development'],
  },
  {
    title: 'Redis',
    competency: 4,
    category: ['Databases', 'Microservices'],
  },
  {
    title: 'RabbitMQ',
    competency: 4,
    category: ['Message Queues', 'Microservices'],
  },
  {
    title: 'Apache Kafka',
    competency: 3,
    category: ['Message Queues', 'Microservices'],
  },
  {
    title: 'Celery',
    competency: 4,
    category: ['Task Queues', 'Python'],
  },
  {
    title: 'ElasticSearch',
    competency: 4,
    category: ['Search Engines', 'Databases'],
  },
  {
    title: 'MyPy',
    competency: 4,
    category: ['Python', 'Type Checking'],
  },
  {
    title: 'Pytest',
    competency: 4,
    category: ['Testing', 'Python'],
  },
  // Architecture Design Skills
  {
    title: 'UML 2.0',
    competency: 4,
    category: ['Architecture', 'Design'],
  },
  {
    title: 'Event-Driven Architecture',
    competency: 4,
    category: ['Architecture', 'Design Patterns'],
  },
  {
    title: 'Domain-Driven Design',
    competency: 4,
    category: ['Architecture', 'Design Patterns'],
  },
  {
    title: 'Microservices Patterns',
    competency: 4,
    category: ['Architecture', 'Microservices'],
  },
  {
    title: 'CQRS',
    competency: 3,
    category: ['Architecture', 'Design Patterns'],
  },
  {
    title: 'Event Sourcing',
    competency: 3,
    category: ['Architecture', 'Design Patterns'],
  },
  // DevOps Skills
  {
    title: 'Docker',
    competency: 5,
    category: ['DevOps', 'Containerization'],
  },
  {
    title: 'Ansible',
    competency: 4,
    category: ['DevOps', 'Automation'],
  },
  {
    title: 'CloudFormation',
    competency: 3,
    category: ['DevOps', 'AWS', 'Infrastructure'],
  },
  {
    title: 'Terraform',
    competency: 3,
    category: ['DevOps', 'Infrastructure'],
  },
  {
    title: 'GitLab CI/CD',
    competency: 4,
    category: ['DevOps', 'CI/CD'],
  },
  {
    title: 'Jenkins',
    competency: 3,
    category: ['DevOps', 'CI/CD'],
  },
  {
    title: 'AWS Lambda',
    competency: 4,
    category: ['DevOps', 'AWS', 'Serverless'],
  },
  {
    title: 'Prometheus',
    competency: 3,
    category: ['DevOps', 'Monitoring'],
  },
].map((skill) => ({ ...skill, category: skill.category.sort() }));

// Skill category colors - coordinated with theme colors
const colors = [
  theme.colors.light.primary,    // #3182ce
  '#37b1f5',                     // Light blue
  '#6968b3',                     // Purple
  theme.colors.light.accent,     // #ed8936
  '#e47272',                     // Light red
  theme.colors.light.success,    // #38a169
  '#3896e2',                     // Blue
  '#c3423f',                     // Red
  '#d75858',                     // Pink
  '#747fff',                     // Light purple
  '#64cb7b',                     // Green
];

const categories = [
  ...new Set(skills.reduce((acc, { category }) => acc.concat(category), [])),
]
  .sort()
  .map((category, index) => ({
    name: category,
    color: colors[index],
  }));

const contact = {
  // email is optional - if left empty Contact section won't show up
  email: 'abhinav.c@live.in',
  phone: '+91-9717185747',
  location: 'Haryana, India',
}

/**
 * Centralized Resume Data Structure
 *
 * This object contains all resume information used by:
 * - Resume web page (/resume)
 * - PDF generation (Professional theme)
 * - JSON Resume export
 *
 * To update your resume:
 * 1. Modify personalInfo for contact details
 * 2. Add new experience entries to the experience array
 * 3. Update education information
 * 4. Add new certifications
 *
 * All changes will automatically reflect in the resume page and PDF downloads.
 */
const resume = {
  personalInfo: {
    name: about.name,
    title: about.role,
    email: contact.email,
    phone: contact.phone,
    location: contact.location,
    summary: about.description,
    linkedin: about.social.linkedin,
    github: about.social.github,
  },

  experience: [
    {
      position: 'Solution Architect II',
      company: 'Calance Software Pvt. Ltd.',
      url: 'https://www.calanceus.com/',
      period: 'May 2024 - Present',
      location: 'Gurgaon, Haryana',
      summary: 'Leading the development of a 3D educational visualization platform that generates interactive 3D scenes from natural language prompts, enabling educators and learners to create dynamic, customizable visualizations for scientific and educational concepts.',
      achievements: [
        'Leading the development of a 3D educational visualization platform that generates interactive 3D scenes from natural language prompts',
        'Developed and implemented integration between internal ERP systems and Workday for White & Case law firm',
        'Led the development of Smart Health platform connecting insurance companies and healthcare providers',
        'Designed comprehensive solutions with standardized APIs for healthcare transaction interoperability'
      ]
    },
    {
      position: 'Senior Consultant',
      company: 'Calance Software Pvt. Ltd.',
      url: 'https://www.calanceus.com/',
      period: 'Mar 2019 - May 2024',
      location: 'Gurgaon, Haryana',
      summary: 'During my tenure as a Senior Consultant at Calance Software Private Limited, I played a pivotal role in driving innovation and delivering data science and enterprise solutions.',
      achievements: [
        'Led clinical trial enrollment forecasting using Poisson Process and pseudo-Monte Carlo simulations',
        'Spearheaded design and development of Analytx, a BPMN-compliant ML/AI pipeline No-Code platform',
        'Designed scalable compute engine in Python, migrating services to leverage deep learning libraries',
        'Created user-friendly UI interface using AngularJS and Oryx.js for non-technical users'
      ]
    },
    {
      position: 'Technology Consultant',
      company: 'Calance Software Pvt. Ltd.',
      url: 'https://www.calanceus.com/',
      period: 'Sep 2013 - Mar 2019',
      location: 'Gurgaon, Haryana',
      summary: 'As a Technology Consultant at Calance Software Private Limited, I collaborated with diverse clients to deliver tailored solutions that aligned with their business needs.',
      achievements: [
        'Developed Event Management Website using .NET 3.5 and MsSQL for White & Case litigation firm',
        'Implemented Ansible playbooks for automated server patching across client environments',
        'Designed hybrid mobile apps using Ionic framework and OpenCart APIs',
        'Created configurable .NET 4.0 API for real-time weighing scale integration via COM ports'
      ]
    }
  ],

  education: [
    {
      degree: 'Bachelor of Engineering',
      institution: 'University of Pune',
      url: 'http://www.unipune.ac.in/',
      period: '2009 - 2013',
      location: 'Pune, Maharashtra',
      studyType: 'Bachelors of Engineering'
    }
  ],

  certifications: [
    {
      title: 'Microsoft Certified Professional',
      id: '880781',
      issuer: 'Microsoft'
    },
    {
      title: 'Cisco Certified Network Associate',
      id: '402094170773ITXK',
      issuer: 'Cisco'
    }
  ],

  skills: skills,
  projects: projects
}

/**
 * Utility Functions for Resume Data Management
 *
 * These functions provide easy access to resume data and help maintain
 * consistency across different components and export formats.
 *
 * Usage examples:
 * - getSkillsByCategory('Data Science') - Get skills in specific category
 * - getProjectsByStack('Python') - Get projects using specific technology
 * - formatForJSON() - Get data in JSON Resume standard format
 */
const getResumeData = () => resume;

const getPersonalInfo = () => resume.personalInfo;

const getExperience = () => resume.experience;

const getEducation = () => resume.education;

const getCertifications = () => resume.certifications;

const getSkillsByCategory = (categoryName) => {
  if (!categoryName) return skills;
  return skills.filter(skill =>
    skill.category.some(cat =>
      cat.toLowerCase().includes(categoryName.toLowerCase())
    )
  );
};

const getProjectsByStack = (stackItem) => {
  if (!stackItem) return projects;
  return projects.filter(project =>
    project.stack.some(tech =>
      tech.toLowerCase().includes(stackItem.toLowerCase())
    )
  );
};

const updatePersonalInfo = (updates) => {
  return { ...resume.personalInfo, ...updates };
};

const addExperience = (newExperience) => {
  return [...resume.experience, newExperience];
};

const addCertification = (newCert) => {
  return [...resume.certifications, newCert];
};

/**
 * Resume Data Formatters
 *
 * Functions to format resume data for different output formats:
 * - formatForJSON() - Standard JSON Resume format for downloads
 * - Can be extended for other formats (XML, etc.)
 */
const formatForJSON = () => ({
  basics: {
    name: resume.personalInfo.name,
    label: resume.personalInfo.title,
    email: resume.personalInfo.email,
    phone: resume.personalInfo.phone,
    summary: resume.personalInfo.summary,
    location: {
      countryCode: "IN",
      region: resume.personalInfo.location
    },
    profiles: [
      {
        network: "LinkedIn",
        username: "abhinavchat",
        url: resume.personalInfo.linkedin
      },
      {
        network: "GitHub",
        username: "abhinavchat",
        url: resume.personalInfo.github
      }
    ]
  },
  work: resume.experience.map(exp => ({
    name: exp.company,
    position: exp.position,
    url: exp.url,
    startDate: exp.period.split(' - ')[0],
    endDate: exp.period.split(' - ')[1] || "Present",
    summary: exp.summary,
    highlights: exp.achievements
  })),
  education: resume.education.map(edu => ({
    institution: edu.institution,
    studyType: edu.degree,
    startDate: edu.period.split(' - ')[0],
    endDate: edu.period.split(' - ')[1]
  })),
  skills: skills.map(skill => ({
    name: skill.title,
    level: skill.competency,
    keywords: skill.category
  }))
});

/**
 * Configuration Getters
 * Helper functions to access configuration values
 */
const getSiteConfig = () => siteConfig;
const getThemeConfig = () => theme;
const getUIConfig = () => uiConfig;
const getResumeConfig = () => resumeConfig;

// Theme helper functions
const getThemeColors = (mode = 'light') => theme.colors[mode];
const getBreakpoint = (size) => theme.breakpoints[size];
const getFontFamily = (type = 'primary') => theme.fonts[type];

// UI helper functions
const getHeaderHeight = (device = 'desktop') => uiConfig.header.height[device];
const isAnimationEnabled = () => uiConfig.animations.enabled;
const shouldShowSection = (section) => uiConfig.navigation[`show${section}Link`];

export {
  // Original exports
  header,
  about,
  projects,
  skills,
  contact,
  categories,
  youtube,
  resume,

  // Configuration exports
  siteConfig,
  theme,
  uiConfig,
  resumeConfig,

  // Utility functions
  getResumeData,
  getPersonalInfo,
  getExperience,
  getEducation,
  getCertifications,
  getSkillsByCategory,
  getProjectsByStack,
  updatePersonalInfo,
  addExperience,
  addCertification,
  formatForJSON,

  // Configuration getters
  getSiteConfig,
  getThemeConfig,
  getUIConfig,
  getResumeConfig,
  getThemeColors,
  getBreakpoint,
  getFontFamily,
  getHeaderHeight,
  isAnimationEnabled,
  shouldShowSection
}
